import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
  FC,
} from 'react';
import { RAILS_MVP } from 'clientState/ab/aliases';
import { useAvailableTransportTypes } from 'clientState/providers/ParsedSearchResponseProvider';
import {
  useTracking,
  useSite,
  createFeature,
} from '@edreamsodigeo/frontend-core';
import { PLANE, TRAIN } from 'mocks/response/TransportType';
import { useTrainSearch } from 'clientState/providers/SearchRequestProvider';
import {
  TransportTypeFilterContextProps,
  TransportTypeProductContextProps,
  UseTransportTypeFilterReturn,
  TransportTypeFilterProviderProps,
} from './types';

const TransportTypeFilterContext =
  createContext<TransportTypeFilterContextProps>([null, () => {}]);
const TransportTypeProductContext =
  createContext<TransportTypeProductContextProps>({
    product: '',
    setProduct: () => {},
  });

const transportTypesTrackingNames = {
  [PLANE]: 'flights',
  [TRAIN]: 'trains',
};

const reducer = (transportTypeValues, action) => {
  switch (action.type) {
    case 'RESET':
      return null;
    case 'FILTER':
      return action.values;
    default:
      return transportTypeValues;
  }
};

const noLoadedHandlers = {
  applyFilter: () => {},
  resetFilter: () => {},
  swap: () => {},
  toggle: () => {},
  transportTypes: [],
};

export const useAppliedTransportTypeFilter = () => {
  const [transportTypes] = useContext(TransportTypeFilterContext);
  return (
    Array.isArray(transportTypes) &&
    transportTypes.some(({ checked }) => !checked)
  );
};

export const useTransportTypeFilter = (): UseTransportTypeFilterReturn => {
  const availableTransportTypes = useAvailableTransportTypes();
  const trainSearch = useTrainSearch();
  const [transportTypes, dispatch] = useContext(TransportTypeFilterContext);
  const { setProduct } = useTransportTypeProduct();
  const { trackEvent } = useTracking();
  const site = useSite();
  const isDESite = ['DE', 'OPDE'].includes(site);
  const initialChecked = (type) => {
    return !(trainSearch && type === PLANE) && !(isDESite && type === TRAIN);
  };

  const transportTypeValues =
    transportTypes ||
    (availableTransportTypes &&
      availableTransportTypes.map((type) => ({
        id: type,
        checked: initialChecked(type),
      })));

  const applyFilter = useMemo(() => {
    return (values) =>
      dispatch({
        type: 'FILTER',
        values,
      });
  }, [dispatch]);

  if (!transportTypeValues) {
    return noLoadedHandlers;
  }

  return {
    transportTypes: transportTypeValues,
    toggle({ target }) {
      const { value, checked } = target;
      const nextTransportTypes = transportTypeValues.map((transportType) => {
        return transportType.id === value
          ? {
              ...transportType,
              checked: !transportType.checked,
            }
          : transportType;
      });
      applyFilter(nextTransportTypes);
      const checkedLabel = checked ? 'checked' : 'unchecked';
      trackEvent({
        action: 'results_filters',
        label: `transport_${checkedLabel}_${transportTypesTrackingNames[value]}`,
      });
    },
    swap: (product) => {
      const nextTransportTypes = transportTypeValues.map((transportType) => {
        return transportType.id === product
          ? {
              ...transportType,
              checked: true,
            }
          : {
              ...transportType,
              checked: false,
            };
      });
      setProduct(product);
      applyFilter(nextTransportTypes);
    },
    resetFilter: () => dispatch({ type: 'RESET' }),
  };
};

const TransportTypeFilterProviderBase: FC<TransportTypeFilterProviderProps> = ({
  hasProductSelector = false,
  children,
}) => {
  const stateAndDispatch = useReducer(reducer, null);
  const trainSearch = useTrainSearch();
  const [product, setProduct] = useState('');

  useEffect(() => {
    if (hasProductSelector && trainSearch) {
      const initialProduct = trainSearch ? 'TRAIN' : 'PLANE';
      setProduct(initialProduct);
    }
  }, [trainSearch, hasProductSelector, setProduct]);

  const productData = useMemo(
    () => ({
      product: product,
      setProduct: setProduct,
    }),
    [product, setProduct]
  );

  return (
    <TransportTypeProductContext.Provider value={productData}>
      <TransportTypeFilterContext.Provider value={stateAndDispatch}>
        {children}
      </TransportTypeFilterContext.Provider>
    </TransportTypeProductContext.Provider>
  );
};

const TransportTypeFilterProviderDefault: FC = (props) => (
  <TransportTypeFilterProviderBase {...props} />
);

const TransportTypeFilterProviderRails: FC = (props) => (
  <TransportTypeFilterProviderBase hasProductSelector={true} {...props} />
);

const TransportTypeFilterProvider = createFeature({
  name: RAILS_MVP,
  variations: [
    TransportTypeFilterProviderDefault,
    TransportTypeFilterProviderRails,
  ],
  version: 1,
});

export const useTransportTypeProduct = () => {
  const context = useContext(TransportTypeProductContext);
  return context;
};

export default TransportTypeFilterProvider;
